
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RefreshCw, Activity, TrendingUp, AlertCircle, CheckCircle, Database } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';

export const RealTimeAnalyticsMonitor: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [refreshCount, setRefreshCount] = useState(0);

  // Monitor system health with simple query
  const { data: refreshStatus } = useQuery({
    queryKey: ['system-health-status'],
    queryFn: async () => {
      // Simple health check using existing tables
      const { data, error } = await supabase
        .from('training_load_history')
        .select('id')
        .limit(1);
      
      if (error) {
        console.warn('System health check failed:', error);
        return { viewsHealthy: false, lastUpdated: new Date(), dataFreshness: 'stale' };
      }

      return {
        viewsHealthy: true,
        lastUpdated: new Date(),
        dataFreshness: 'current',
        systemOnline: true
      };
    },
    refetchInterval: 30000, // Check every 30 seconds
    enabled: !!user,
  });

  // Real-time activity monitoring
  const { data: recentActivity } = useQuery({
    queryKey: ['recent-activity-monitor', user?.id],
    queryFn: async () => {
      if (!user) return null;

      const oneDayAgo = new Date();
      oneDayAgo.setDate(oneDayAgo.getDate() - 1);

      const { data: runningData, error: runningError } = await supabase
        .from('running_performance')
        .select('id, recorded_date, data_source, created_at')
        .eq('user_id', user.id)
        .gte('created_at', oneDayAgo.toISOString())
        .order('created_at', { ascending: false })
        .limit(5);

      const { data: fitnessData, error: fitnessError } = await supabase
        .from('general_fitness')
        .select('id, recorded_date, data_source, created_at')
        .eq('user_id', user.id)
        .gte('created_at', oneDayAgo.toISOString())
        .order('created_at', { ascending: false })
        .limit(5);

      return {
        recentRunning: runningData || [],
        recentFitness: fitnessData || [],
        totalRecentActivities: (runningData?.length || 0) + (fitnessData?.length || 0)
      };
    },
    refetchInterval: 60000, // Check every minute
    enabled: !!user,
  });

  // Manual refresh function
  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    setRefreshCount(prev => prev + 1);
    
    try {
      // Refresh materialized views
      await supabase.rpc('refresh_analytics_views');
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['training-load-analysis'] });
      queryClient.invalidateQueries({ queryKey: ['environmental-performance'] });
      queryClient.invalidateQueries({ queryKey: ['materialized-view-status'] });
      
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to refresh analytics:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      handleManualRefresh();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-6">
      {/* System Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Database className="h-4 w-4 text-blue-600" />
              Analytics Engine
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                <CheckCircle className="h-3 w-3 mr-1" />
                Online
              </Badge>
              <span className="text-sm text-muted-foreground">
                {refreshCount} refreshes
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Last: {lastRefresh.toLocaleTimeString()}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className="h-4 w-4 text-purple-600" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{recentActivity?.totalRecentActivities || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Activities in last 24h
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-600" />
              Data Freshness
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Badge variant="secondary" className="bg-green-100 text-green-800">
              {refreshStatus?.dataFreshness || 'Current'}
            </Badge>
            <p className="text-xs text-muted-foreground mt-1">
              Views synchronized
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <RefreshCw className={`h-4 w-4 text-blue-600 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh Control
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button 
              size="sm" 
              onClick={handleManualRefresh} 
              disabled={isRefreshing}
              className="w-full"
            >
              {isRefreshing ? 'Refreshing...' : 'Refresh Now'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Real-time Activity Feed */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Real-time Activity Monitor
          </CardTitle>
          <CardDescription>
            Latest activities and data synchronization status
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentActivity?.totalRecentActivities > 0 ? (
              <div className="space-y-3">
                {recentActivity.recentRunning.map((activity) => (
                  <div key={activity.id} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded">
                    <div className="flex items-center gap-3">
                      <Activity className="h-4 w-4 text-blue-600" />
                      <div>
                        <div className="text-sm font-medium">Running Activity</div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(activity.recorded_date).toLocaleDateString()} • {activity.data_source}
                        </div>
                      </div>
                    </div>
                    <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                      {new Date(activity.created_at).toLocaleTimeString()}
                    </Badge>
                  </div>
                ))}
                
                {recentActivity.recentFitness.map((activity) => (
                  <div key={activity.id} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded">
                    <div className="flex items-center gap-3">
                      <TrendingUp className="h-4 w-4 text-green-600" />
                      <div>
                        <div className="text-sm font-medium">Fitness Activity</div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(activity.recorded_date).toLocaleDateString()} • {activity.data_source}
                        </div>
                      </div>
                    </div>
                    <Badge variant="secondary" className="bg-green-100 text-green-800">
                      {new Date(activity.created_at).toLocaleTimeString()}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No recent activities in the last 24 hours</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* System Health Alerts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            System Health & Alerts
          </CardTitle>
          <CardDescription>
            Monitoring analytics performance and data quality
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                <strong>Analytics Engine:</strong> All materialized views are current and synchronized.
              </AlertDescription>
            </Alert>

            <Alert className="border-blue-200 bg-blue-50">
              <Database className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                <strong>Data Pipeline:</strong> Real-time sync active. Next automatic refresh in {Math.ceil((5 * 60 * 1000 - (Date.now() - lastRefresh.getTime())) / 60000)} minutes.
              </AlertDescription>
            </Alert>

            {refreshStatus?.viewsHealthy && (
              <Alert className="border-purple-200 bg-purple-50">
                <TrendingUp className="h-4 w-4 text-purple-600" />
                <AlertDescription className="text-purple-800">
                  <strong>Performance:</strong> Analytics queries are optimized and performing well.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Refresh Statistics */}
      <Card>
        <CardHeader>
          <CardTitle>Refresh Statistics</CardTitle>
          <CardDescription>
            Analytics refresh history and performance metrics
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded">
              <div className="text-2xl font-bold text-gray-800">{refreshCount}</div>
              <div className="text-sm text-gray-600">Total Refreshes</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded">
              <div className="text-2xl font-bold text-gray-800">~5min</div>
              <div className="text-sm text-gray-600">Auto Refresh Interval</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded">
              <div className="text-2xl font-bold text-gray-800">3</div>
              <div className="text-sm text-gray-600">Materialized Views</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
